import PropTypes from "prop-types";
import React, { useState, useRef } from "react";
import CourseQuiz from "../../components/CourseQuiz/CourseQuiz";
import { activityTypes } from "../../constants";
import CourseHomework from "../../components/ContentViewer/CourseHomework";
import ContentPlayer from "../../components/ContentViewer/ContentPlayer";
import ContentPlayerBottomNavbar from "../../components/ContentViewer/ContentPlayerBottomNavbar";
import ContentViewerNavigationButtons from "../../components/ContentViewer/ContentViewerNavigationButtons";
import ContentViewerHeader from "../../components/ContentViewer/ContentViewerHeader";
import { useCourseContentable } from "../../hooks/courseContentable";
import CourseOverviewPage from "../../components/ContentViewer/CourseOverviewPage";
import FullPageLayout from "../../components/FullPageLayout";
import { Box } from "@mui/material";
import ShortCourseCompletedModal from "../../components/ContentViewer/ShortCourseCompletedModal";

const ActivityPreview = ({
  activity,
  courseData,
  courseSectionTitle,
  onNextActivityClick,
  onPrevActivityClick,
  onGetStartedClick,
  onGoToSurveyClick,
  onGoToNextSectionClick,
  onViewOutlineClick,
  isFirstActivity,
  isLastActivity,
  isLastSection,
  nextCourseSectionTitle,
  hasSurvey
}) => {
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const { courseContentable } = useCourseContentable(activity.id, true);
  const [fullScreen, setFullScreen] = useState(false);
  let iframeRef = useRef(null);
  if (activity) {
    const isQuiz = activity.type === activityTypes.quiz;
    const isSurvey = activity.type === activityTypes.survey;
    const isOverview = activity.type === activityTypes.json;

    const showQuizPlayer =
      (isQuiz || isSurvey) && courseContentable && courseContentable.quiz;

    const handleExitShortCourseClick = () => {
      setShowCompletionModal(true);
    };

    const handleGoToSurveyClick = () => {
      setShowCompletionModal(false);
      onGoToSurveyClick();
    };

    const handleGoToNextSectionClick = () => {
      setShowCompletionModal(false);
      onGoToNextSectionClick();
    };

    const navigationButtons = (
      <ContentViewerNavigationButtons
        lastActivity={isLastActivity}
        previousLink={!isFirstActivity}
        handleNextActivity={onNextActivityClick}
        handlePrevActivity={onPrevActivityClick}
        onExitShortCourse={handleExitShortCourseClick}
        showSubmitQuizButton={isQuiz || isSurvey}
      />
    );

    const contentViewerContent = (
      <>
        {showQuizPlayer ? (
          <CourseQuiz
            submitted={false}
            quiz={courseContentable.quiz}
            questions={courseContentable.questions}
            onSubmitQuiz={onNextActivityClick}
            survey={isSurvey}
            navigationButtons={navigationButtons}
            questionsPerPage={courseContentable.questionsPerPage}
            learnerAnswers={[]}
          />
        ) : activity.type === activityTypes.homework && courseContentable ? (
          <CourseHomework
            homework={courseContentable.extendable}
            isPreviewMode
          />
        ) : (
          <ContentPlayer
            contentLink={activity.url}
            openInNewTab={activity.openInNewTab}
            contentSettings={activity.settings}
            downloadLink={activity.downloadLink}
            contentType={activity.type}
            mimeType={activity.mimeType}
            fileType={activity.fileType}
            onFullScreenChange={setFullScreen}
            fullScreen={fullScreen}
            iframeRef={iframeRef}
          />
        )}
      </>
    );

    return (
      <>
        {isOverview ? (
          <FullPageLayout title={courseData.title}>
            <Box mt={5}>
              <CourseOverviewPage
                image={courseData.image}
                passingScore={courseData.passingScore}
                overviewText={courseData.overviewText}
                duration={courseData.duration}
                passFail={!courseData.recordsScore}
                courseType={courseData.trainingObjectType}
                description={courseData.courseDescription}
                onViewOutlineClick={onViewOutlineClick}
                onGetStartedClick={onGetStartedClick}
              />
            </Box>
          </FullPageLayout>
        ) : (
          <Box>
            {!isOverview && (
              <ContentViewerHeader
                quiz={isQuiz}
                activityTitle={activity.title}
                contentType={activity.type}
                lastVisited={null}
                survey={isSurvey}
                navigationButtons={navigationButtons}
              />
            )}
            {contentViewerContent}
          </Box>
        )}
        {!showQuizPlayer && !isOverview && (
          <ContentPlayerBottomNavbar navButtons={navigationButtons} />
        )}
        <ShortCourseCompletedModal
          showDialog={showCompletionModal}
          courseTitle={courseData.title}
          moduleCompleted={isLastActivity}
          moduleTitle={courseSectionTitle}
          nextModule={nextCourseSectionTitle}
          courseCompleted={isLastSection && isLastActivity}
          hasCertificate={courseData.hasCertificate}
          recordsScore={courseData.recordsScore}
          unfinishedResourceSurvey={hasSurvey}
          onInternalCourseSurvey={() => handleGoToSurveyClick()}
          isCoursePassed
          isPreviewMode
          onExitToLearningPath={() => window.close()}
          onNextActivity={() => handleGoToNextSectionClick()}
        />
      </>
    );
  } else {
    return null;
  }
};
export default ActivityPreview;

ActivityPreview.propTypes = {
  activity: PropTypes.shape({
    url: PropTypes.string,
    openInNewTab: PropTypes.bool,
    id: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    mimeType: PropTypes.string,
    contentType: PropTypes.string,
    downloadLink: PropTypes.string
  }),
  courseData: PropTypes.shape({
    title: PropTypes.string
  }),
  courseSectionTitle: PropTypes.string,
  onNextActivityClick: PropTypes.func,
  onPrevActivityClick: PropTypes.func,
  onGetStartedClick: PropTypes.func,
  onGoToSurveyClick: PropTypes.func,
  onGoToNextSectionClick: PropTypes.func,
  onViewOutlineClick: PropTypes.func,
  isFirstActivity: PropTypes.bool,
  isLastActivity: PropTypes.bool,
  isLastSection: PropTypes.bool,
  hasSurvey: PropTypes.bool,
  nextCourseSectionTitle: PropTypes.string
};
